import {
    APP_ENV,
    APP_VERSION,
    ERROR_LOGGING_FLAG,
} from "constants/appConstants";

export const ERROR_SOURCE = {
    RENDER: "render",
    WINDOW_ERROR: "window-error",
    UNHANDLED_REJECTION: "unhandled-rejection",
    API: "api",
};

export const ERROR_SEVERITY = {
    FATAL: "fatal",
    ERROR: "error",
    WARNING: "warning",
};

const MAX_MESSAGE_LENGTH = 2000;
const MAX_STACK_LENGTH = 4000;
const RATE_LIMIT_WINDOW_MS = 10_000;
const RATE_LIMIT_MAX_PER_WINDOW = 5;
const SESSION_MAX_WRITES = 20;

const seen = new Set();
let windowTimestamps = [];
let sessionCount = 0;

const isEnabled = () =>
    APP_ENV === "production" || ERROR_LOGGING_FLAG === "on";

// Crash loops (e.g. a render error that retriggers on every re-render) can
// otherwise write hundreds of docs in seconds. Checked before any dynamic
// import fires, so a rate-limited error never triggers the Firestore chunk fetch.
const isRateLimited = () => {
    if (sessionCount >= SESSION_MAX_WRITES) return true;
    const now = Date.now();
    windowTimestamps = windowTimestamps.filter(
        (ts) => now - ts <= RATE_LIMIT_WINDOW_MS,
    );
    if (windowTimestamps.length >= RATE_LIMIT_MAX_PER_WINDOW) return true;
    windowTimestamps.push(now);
    sessionCount += 1;
    return false;
};

const isDuplicate = (message, stack) => {
    const key = `${message}::${String(stack).slice(0, 200)}`;
    if (seen.has(key)) return true;
    seen.add(key);
    return false;
};

const getOpaqueUserId = () => {
    try {
        const raw = localStorage.getItem("u_user_data");
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed?.id ?? parsed?.userId ?? null;
    } catch {
        return null;
    }
};

// No static "firebase" import here — Firestore is only fetched the first
// time an error actually needs logging, so it never enters the main bundle.
export const logFrontendError = async ({
    message,
    stack,
    componentStack = null,
    source,
    severity = ERROR_SEVERITY.ERROR,
    refId = null,
    url,
}) => {
    if (!isEnabled()) return;
    if (isDuplicate(message, stack)) return;
    if (isRateLimited()) return;

    try {
        const [{ db }, { addDoc, collection, serverTimestamp }] =
            await Promise.all([
                import("configs/firebaseConfig"),
                import("firebase/firestore"),
            ]);

        await addDoc(collection(db, "frontendErrors"), {
            message: String(message).slice(0, MAX_MESSAGE_LENGTH),
            stack: stack ? String(stack).slice(0, MAX_STACK_LENGTH) : "",
            componentStack,
            source,
            severity,
            url: url ?? `${window.location.origin}${window.location.pathname}`,
            route: window.location.pathname,
            userAgent: navigator.userAgent,
            environment: APP_ENV || "development",
            appVersion: APP_VERSION,
            userId: getOpaqueUserId(),
            refId,
            createdAt: serverTimestamp(),
        });
    } catch {
        // Logging must never crash the app it is trying to report on.
    }
};
